import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Loader2, Search, AlertTriangle } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import ProcurementService from '@/api/procurement.service';

interface GoodsReceivedItem {
  id?: number;
  purchaseOrderItemId: string;
  itemId: string;
  itemName: string;
  description: string;
  orderedQuantity: number;
  receivedQuantity: number;
  acceptedQuantity: number;
  rejectedQuantity: number;
  unitPrice: number;
  qualityStatus: 'pending' | 'passed' | 'failed' | 'partial';
  qualityNotes: string;
  discrepancyReason: string;
}

interface GoodsReceivedFormData {
  grvNumber: string;
  purchaseOrderId: string;
  receivedDate: string;
  receivedBy: string;
  supplierDeliveryNote: string;
  transportDetails: string;
  status: 'draft' | 'pending_verification' | 'verified' | 'posted' | 'rejected';
  qualityCheckRequired: boolean;
  qualityCheckBy: string;
  qualityCheckDate: string;
  notes: string;
  items: GoodsReceivedItem[];
}

interface GoodsReceivedFormProps {
  mode: 'create' | 'edit';
  initialData?: any;
  onSubmit: (data: GoodsReceivedFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const GoodsReceivedForm: React.FC<GoodsReceivedFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<GoodsReceivedFormData>({
    grvNumber: '',
    purchaseOrderId: '',
    receivedDate: new Date().toISOString().split('T')[0],
    receivedBy: '',
    supplierDeliveryNote: '',
    transportDetails: '',
    status: 'draft',
    qualityCheckRequired: false,
    qualityCheckBy: '',
    qualityCheckDate: '',
    notes: '',
    items: []
  });

  const [availablePOs, setAvailablePOs] = useState<any[]>([]);
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [isLoadingPOs, setIsLoadingPOs] = useState(false);
  const [isLoadingPOItems, setIsLoadingPOItems] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        grvNumber: initialData.grvNumber || '',
        purchaseOrderId: initialData.purchaseOrderId?.toString() || '',
        receivedDate: initialData.receivedDate ? new Date(initialData.receivedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        receivedBy: initialData.receivedBy || '',
        supplierDeliveryNote: initialData.supplierDeliveryNote || '',
        transportDetails: initialData.transportDetails || '',
        status: initialData.status || 'draft',
        qualityCheckRequired: initialData.qualityCheckRequired || false,
        qualityCheckBy: initialData.qualityCheckBy || '',
        qualityCheckDate: initialData.qualityCheckDate ? new Date(initialData.qualityCheckDate).toISOString().split('T')[0] : '',
        notes: initialData.notes || '',
        items: initialData.items || []
      });
      if (initialData.purchaseOrder) {
        setSelectedPO(initialData.purchaseOrder);
      }
    }
    fetchAvailablePOs();
  }, [mode, initialData]);

  const fetchAvailablePOs = async () => {
    try {
      setIsLoadingPOs(true);
      const response = await ProcurementService.getPurchaseOrders(businessId, { status: 'sent' });
      if (response.data?.success) {
        setAvailablePOs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load available purchase orders",
      });
    } finally {
      setIsLoadingPOs(false);
    }
  };

  const fetchPOItems = async (poId: string) => {
    try {
      setIsLoadingPOItems(true);
      const response = await ProcurementService.getPurchaseOrder(businessId, parseInt(poId));
      if (response.data?.success) {
        const po = response.data.data;
        setSelectedPO(po);
        
        // Convert PO items to GRV items
        const grvItems: GoodsReceivedItem[] = po.items?.map((item: any) => ({
          purchaseOrderItemId: item.id?.toString() || '',
          itemId: item.itemId?.toString() || '',
          itemName: item.name || item.itemName || '',
          description: item.description || '',
          orderedQuantity: item.quantity || 0,
          receivedQuantity: 0,
          acceptedQuantity: 0,
          rejectedQuantity: 0,
          unitPrice: item.unitPrice || 0,
          qualityStatus: 'pending',
          qualityNotes: '',
          discrepancyReason: ''
        })) || [];

        setFormData(prev => ({ ...prev, items: grvItems }));
      }
    } catch (error) {
      console.error('Error fetching PO items:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load purchase order items",
      });
    } finally {
      setIsLoadingPOItems(false);
    }
  };

  const handleInputChange = (field: keyof GoodsReceivedFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePOSelection = (poId: string) => {
    handleInputChange('purchaseOrderId', poId);
    if (poId) {
      fetchPOItems(poId);
    } else {
      setSelectedPO(null);
      setFormData(prev => ({ ...prev, items: [] }));
    }
  };

  const updateItem = (index: number, field: keyof GoodsReceivedItem, value: any) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Auto-calculate accepted/rejected quantities when received quantity changes
    if (field === 'receivedQuantity') {
      const receivedQty = parseFloat(value) || 0;
      updatedItems[index].acceptedQuantity = receivedQty;
      updatedItems[index].rejectedQuantity = 0;
      
      // Update quality status based on received vs ordered quantity
      const orderedQty = updatedItems[index].orderedQuantity;
      if (receivedQty === orderedQty) {
        updatedItems[index].qualityStatus = 'passed';
      } else if (receivedQty > 0 && receivedQty < orderedQty) {
        updatedItems[index].qualityStatus = 'partial';
      } else if (receivedQty === 0) {
        updatedItems[index].qualityStatus = 'failed';
      }
    }

    // Ensure accepted + rejected = received
    if (field === 'acceptedQuantity' || field === 'rejectedQuantity') {
      const acceptedQty = field === 'acceptedQuantity' ? (parseFloat(value) || 0) : updatedItems[index].acceptedQuantity;
      const rejectedQty = field === 'rejectedQuantity' ? (parseFloat(value) || 0) : updatedItems[index].rejectedQuantity;
      const receivedQty = updatedItems[index].receivedQuantity;
      
      if (acceptedQty + rejectedQty !== receivedQty) {
        if (field === 'acceptedQuantity') {
          updatedItems[index].rejectedQuantity = Math.max(0, receivedQty - acceptedQty);
        } else {
          updatedItems[index].acceptedQuantity = Math.max(0, receivedQty - rejectedQty);
        }
      }
    }

    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.grvNumber.trim()) {
      newErrors.grvNumber = 'GRV number is required';
    }

    if (!formData.purchaseOrderId) {
      newErrors.purchaseOrderId = 'Purchase order selection is required';
    }

    if (!formData.receivedDate) {
      newErrors.receivedDate = 'Received date is required';
    }

    if (!formData.receivedBy.trim()) {
      newErrors.receivedBy = 'Received by is required';
    }

    if (formData.qualityCheckRequired && !formData.qualityCheckBy.trim()) {
      newErrors.qualityCheckBy = 'Quality check by is required when quality check is enabled';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one item must be received';
    }

    // Validate items
    formData.items.forEach((item, index) => {
      if (item.receivedQuantity < 0) {
        newErrors[`item_${index}_receivedQuantity`] = 'Received quantity cannot be negative';
      }
      if (item.acceptedQuantity < 0) {
        newErrors[`item_${index}_acceptedQuantity`] = 'Accepted quantity cannot be negative';
      }
      if (item.rejectedQuantity < 0) {
        newErrors[`item_${index}_rejectedQuantity`] = 'Rejected quantity cannot be negative';
      }
      if (item.acceptedQuantity + item.rejectedQuantity !== item.receivedQuantity) {
        newErrors[`item_${index}_quantities`] = 'Accepted + Rejected must equal Received quantity';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fix the errors before submitting",
      });
      return;
    }

    onSubmit(formData);
  };

  const getQualityStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'outline' as const, label: 'Pending' },
      passed: { variant: 'default' as const, label: 'Passed' },
      failed: { variant: 'destructive' as const, label: 'Failed' },
      partial: { variant: 'secondary' as const, label: 'Partial' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'outline' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTotalReceivedValue = () => {
    return formData.items.reduce((sum, item) => sum + (item.acceptedQuantity * item.unitPrice), 0);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Goods Received Information</CardTitle>
          <CardDescription>Basic details about the goods received voucher</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="grvNumber">GRV Number *</Label>
              <Input
                id="grvNumber"
                value={formData.grvNumber}
                onChange={(e) => handleInputChange('grvNumber', e.target.value)}
                placeholder="Enter GRV number"
                className={errors.grvNumber ? 'border-red-500' : ''}
              />
              {errors.grvNumber && (
                <p className="text-sm text-red-500">{errors.grvNumber}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="purchaseOrderId">Purchase Order *</Label>
              <Select 
                value={formData.purchaseOrderId} 
                onValueChange={handlePOSelection}
                disabled={isLoadingPOs}
              >
                <SelectTrigger className={errors.purchaseOrderId ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select purchase order" />
                </SelectTrigger>
                <SelectContent>
                  {availablePOs.map((po) => (
                    <SelectItem key={po.id} value={po.id.toString()}>
                      {po.orderNumber} - {po.supplier?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.purchaseOrderId && (
                <p className="text-sm text-red-500">{errors.purchaseOrderId}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="receivedDate">Received Date *</Label>
              <Input
                id="receivedDate"
                type="date"
                value={formData.receivedDate}
                onChange={(e) => handleInputChange('receivedDate', e.target.value)}
                className={errors.receivedDate ? 'border-red-500' : ''}
              />
              {errors.receivedDate && (
                <p className="text-sm text-red-500">{errors.receivedDate}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="receivedBy">Received By *</Label>
              <Input
                id="receivedBy"
                value={formData.receivedBy}
                onChange={(e) => handleInputChange('receivedBy', e.target.value)}
                placeholder="Enter receiver name"
                className={errors.receivedBy ? 'border-red-500' : ''}
              />
              {errors.receivedBy && (
                <p className="text-sm text-red-500">{errors.receivedBy}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="supplierDeliveryNote">Supplier Delivery Note</Label>
              <Input
                id="supplierDeliveryNote"
                value={formData.supplierDeliveryNote}
                onChange={(e) => handleInputChange('supplierDeliveryNote', e.target.value)}
                placeholder="Enter delivery note number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_verification">Pending Verification</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="posted">Posted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="transportDetails">Transport Details</Label>
            <Textarea
              id="transportDetails"
              value={formData.transportDetails}
              onChange={(e) => handleInputChange('transportDetails', e.target.value)}
              placeholder="Enter transport and delivery details"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional notes or comments"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Quality Check Section */}
      <Card>
        <CardHeader>
          <CardTitle>Quality Control</CardTitle>
          <CardDescription>Quality check requirements and details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="qualityCheckRequired"
              checked={formData.qualityCheckRequired}
              onCheckedChange={(checked) => handleInputChange('qualityCheckRequired', checked)}
            />
            <Label htmlFor="qualityCheckRequired">Quality check required</Label>
          </div>

          {formData.qualityCheckRequired && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="qualityCheckBy">Quality Check By *</Label>
                <Input
                  id="qualityCheckBy"
                  value={formData.qualityCheckBy}
                  onChange={(e) => handleInputChange('qualityCheckBy', e.target.value)}
                  placeholder="Enter quality checker name"
                  className={errors.qualityCheckBy ? 'border-red-500' : ''}
                />
                {errors.qualityCheckBy && (
                  <p className="text-sm text-red-500">{errors.qualityCheckBy}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="qualityCheckDate">Quality Check Date</Label>
                <Input
                  id="qualityCheckDate"
                  type="date"
                  value={formData.qualityCheckDate}
                  onChange={(e) => handleInputChange('qualityCheckDate', e.target.value)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Items Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Received Items</CardTitle>
              <CardDescription>Items received from the selected purchase order</CardDescription>
            </div>
            {selectedPO && (
              <div className="text-sm text-muted-foreground">
                PO: {selectedPO.orderNumber} | Supplier: {selectedPO.supplier?.name}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {errors.items && (
            <p className="text-sm text-red-500 mb-4">{errors.items}</p>
          )}
          
          {isLoadingPOItems ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading purchase order items...</span>
            </div>
          ) : formData.items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Select a purchase order to load items for receiving.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Ordered Qty</TableHead>
                    <TableHead>Received Qty</TableHead>
                    <TableHead>Accepted Qty</TableHead>
                    <TableHead>Rejected Qty</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Quality Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.itemName}</p>
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>{item.orderedQuantity}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.receivedQuantity}
                          onChange={(e) => updateItem(index, 'receivedQuantity', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          className={`w-20 ${errors[`item_${index}_receivedQuantity`] ? 'border-red-500' : ''}`}
                        />
                        {errors[`item_${index}_receivedQuantity`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`item_${index}_receivedQuantity`]}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.acceptedQuantity}
                          onChange={(e) => updateItem(index, 'acceptedQuantity', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          className={`w-20 ${errors[`item_${index}_acceptedQuantity`] ? 'border-red-500' : ''}`}
                        />
                        {errors[`item_${index}_acceptedQuantity`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`item_${index}_acceptedQuantity`]}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.rejectedQuantity}
                          onChange={(e) => updateItem(index, 'rejectedQuantity', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          className={`w-20 ${errors[`item_${index}_rejectedQuantity`] ? 'border-red-500' : ''}`}
                        />
                        {errors[`item_${index}_rejectedQuantity`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`item_${index}_rejectedQuantity`]}</p>
                        )}
                      </TableCell>
                      <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell>{formatCurrency(item.acceptedQuantity * item.unitPrice)}</TableCell>
                      <TableCell>
                        <Select
                          value={item.qualityStatus}
                          onValueChange={(value) => updateItem(index, 'qualityStatus', value)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="passed">Passed</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                            <SelectItem value="partial">Partial</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Quality Notes and Discrepancy Reasons */}
              <div className="space-y-4">
                <h4 className="font-medium">Quality Notes & Discrepancy Reasons</h4>
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor={`qualityNotes_${index}`}>
                        Quality Notes for {item.itemName}
                      </Label>
                      <Textarea
                        id={`qualityNotes_${index}`}
                        value={item.qualityNotes}
                        onChange={(e) => updateItem(index, 'qualityNotes', e.target.value)}
                        placeholder="Enter quality inspection notes"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`discrepancyReason_${index}`}>
                        Discrepancy Reason for {item.itemName}
                      </Label>
                      <Textarea
                        id={`discrepancyReason_${index}`}
                        value={item.discrepancyReason}
                        onChange={(e) => updateItem(index, 'discrepancyReason', e.target.value)}
                        placeholder="Enter reason for any discrepancies"
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-6 pt-4 border-t">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Items</p>
                    <p className="text-lg font-semibold">{formData.items.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Received</p>
                    <p className="text-lg font-semibold">
                      {formData.items.reduce((sum, item) => sum + item.receivedQuantity, 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Accepted</p>
                    <p className="text-lg font-semibold">
                      {formData.items.reduce((sum, item) => sum + item.acceptedQuantity, 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Value</p>
                    <p className="text-lg font-semibold">{formatCurrency(getTotalReceivedValue())}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || formData.items.length === 0}
          className="flex items-center gap-2"
        >
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {mode === 'create' ? 'Create GRV' : 'Update GRV'}
        </Button>
      </div>
    </form>
  );
};

export default GoodsReceivedForm; 